// 재난문자 API 연동 서비스
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// 재난문자 목록 조회 API 호출
const fetchDisasterMessages = async (params = {}) => {
  try {
    // 기본 파라미터 설정
    const defaultParams = {
      serviceKey: process.env.REACT_APP_DISASTER_SERVICE_KEY || 'N5NPL1RPIPX23Z9N',
      numOfRows: 10,
      pageNo: 1,
      returnType: 'json'
    };

    // 기본 파라미터와 사용자 파라미터 병합
    const queryParams = { ...defaultParams, ...params };

    // URL 쿼리 파라미터 생성
    const urlParams = new URLSearchParams();

    // 모든 파라미터 추가
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] !== undefined && queryParams[key] !== null && queryParams[key] !== '') {
        urlParams.append(key, queryParams[key]);
      }
    });

    console.log('재난문자 API 요청 파라미터:', queryParams);

    // API 요청
    const response = await fetch(`${API_URL}/api/disaster?${urlParams.toString()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    // API 응답 확인
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || `HTTP 오류: ${response.status}`);
    }

    // JSON 응답 파싱
    const responseData = await response.json();
    console.log('재난문자 API 응답:', responseData);

    // API 응답 데이터 처리
    let items = [];
    let totalCount = 0;

    // 다양한 응답 구조 처리
    if (responseData.response && responseData.response.body) {
      // 표준 공공데이터 포털 응답 구조
      const body = responseData.response.body;
      totalCount = body.totalCount || 0;
      items = body.items?.item || [];

      // 단일 항목인 경우 배열로 변환
      if (items && !Array.isArray(items)) {
        items = [items];
      }
    } else if (responseData.items && responseData.items.item) {
      // 간소화된 응답 구조
      items = responseData.items.item;
      totalCount = responseData.totalCount || responseData.items.totalCount || items.length;

      // 단일 항목인 경우 배열로 변환
      if (items && !Array.isArray(items)) {
        items = [items];
      }
    } else {
      // 기타 응답 구조 (가능한 경우 처리)
      if (Array.isArray(responseData)) {
        items = responseData;
        totalCount = responseData.length;
      } else if (typeof responseData === 'object') {
        items = [responseData];
        totalCount = 1;
      }
    }

    // 데이터를 날짜 기준 내림차순으로 정렬 (최신순)
    items = sortItemsByDate(items);

    return {
      success: true,
      data: items,
      totalCount: totalCount,
      pageNo: parseInt(queryParams.pageNo) || 1,
      numOfRows: parseInt(queryParams.numOfRows) || 10
    };
  } catch (error) {
    console.error('재난문자 API 요청 오류:', error);
    return {
      success: false,
      error: error.message || '재난문자 정보를 불러오는 데 실패했습니다.',
      data: []
    };
  }
};

// 날짜 정렬을 위한 함수
const parseDateForSorting = (dateStr) => {
  if (!dateStr) return 0;

  // CRT_DT 형식 (2023/09/16 11:09:49) 처리
  if (dateStr.includes('/')) {
    const parts = dateStr.split(' ');
    const dateParts = parts[0].split('/');
    const timeParts = parts.length > 1 ? parts[1].split(':') : ['00', '00', '00'];

    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // JavaScript month is 0-based
    const day = parseInt(dateParts[2]);
    const hour = parseInt(timeParts[0]);
    const minute = parseInt(timeParts[1]);
    const second = parseInt(timeParts[2]);

    return new Date(year, month, day, hour, minute, second).getTime();
  }

  // YYYYMMDD 형식 처리
  if (dateStr.length === 8) {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1; // JavaScript month is 0-based
    const day = parseInt(dateStr.substring(6, 8));
    return new Date(year, month, day).getTime();
  }

  // "2025-04-04 15:10:07" 형식 처리
  if (dateStr.includes('-') || dateStr.includes('/')) {
    return new Date(dateStr).getTime();
  }

  return 0;
};

// 날짜 기준 내림차순 정렬 (최신순)
const sortItemsByDate = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  return [...items].sort((a, b) => {
    const dateA = parseDateForSorting(a.CRT_DT || a.create_date || '');
    const dateB = parseDateForSorting(b.CRT_DT || b.create_date || '');
    return dateB - dateA; // 내림차순 정렬 (최신순)
  });
};

// 검색어로 재난문자 필터링
const filterDisasterMessages = (items, filters = {}) => {
  console.log('필터링 시작. 원본 데이터:', items);
  console.log('필터:', filters);

  // 메시지 데이터 추출 - 다양한 데이터 구조 처리
  let messagesToFilter = [];

  try {
    // 1. body 배열이 있는 경우
    if (items && items.body) {
      if (Array.isArray(items.body)) {
        console.log('필터링: body 배열 사용. 길이:', items.body.length);
        messagesToFilter = [...items.body];
      } else {
        console.log('필터링: body 객체를 배열로 변환');
        messagesToFilter = [items.body];
      }
    }
    // 2. 공공데이터 포털 응답 형식
    else if (items && items.items && items.items.item) {
      if (Array.isArray(items.items.item)) {
        console.log('필터링: items.item 배열 사용');
        messagesToFilter = [...items.items.item];
      } else {
        console.log('필터링: items.item 객체를 배열로 변환');
        messagesToFilter = [items.items.item];
      }
    }

    // 3. 배열 형태
    else if (Array.isArray(items)) {
      console.log('필터링: 배열 그대로 사용. 길이:', items.length);
      messagesToFilter = [...items];
    }

    // 4. 객체 형태
    else if (items && typeof items === 'object') {
      console.log('필터링: 객체를 배열로 변환');
      messagesToFilter = [items];
    }

    // 유효하지 않은 데이터
    else {
      console.warn('필터링: 유효하지 않은 데이터 형식');
      return [];
    }

    console.log('필터링 전 메시지 수:', messagesToFilter.length);

    if (messagesToFilter.length === 0) {
      console.warn('필터링할 메시지가 없습니다');
      return [];
    }

    let filteredItems = [...messagesToFilter];

    // 키워드 필터링
    if (filters.keyword) {
      const keyword = filters.keyword.toLowerCase();
      filteredItems = filteredItems.filter(item => {
        const message = (item.MSG_CN || item.msg || '').toLowerCase();
        const region = (item.RCPTN_RGN_NM || item.areaName || '').toLowerCase();
        const type = (item.DST_SE_NM || item.type || '').toLowerCase();

        return message.includes(keyword) ||
          region.includes(keyword) ||
          type.includes(keyword);
      });
      console.log('키워드 필터링 후 메시지 수:', filteredItems.length);
    }

    // 지역 필터링
    if (filters.region) {
      const region = filters.region.toLowerCase();
      filteredItems = filteredItems.filter(item => {
        const itemRegion = (item.RCPTN_RGN_NM || item.areaName || '').toLowerCase();
        return itemRegion.includes(region);
      });
      console.log('지역 필터링 후 메시지 수:', filteredItems.length);
    }

    // 재난 유형 필터링
    if (filters.type) {
      const type = filters.type.toLowerCase();
      filteredItems = filteredItems.filter(item => {
        const itemType = (item.DST_SE_NM || item.type || '').toLowerCase();
        return itemType.includes(type);
      });
      console.log('재난 유형 필터링 후 메시지 수:', filteredItems.length);
    }

    // 발신 기관 필터링
    if (filters.agency) {
      const agency = filters.agency.toLowerCase();
      filteredItems = filteredItems.filter(item => {
        const message = (item.MSG_CN || item.msg || '').toLowerCase();
        const matches = message.match(/\[(.*?)\]/);
        if (matches && matches.length > 1) {
          const itemAgency = matches[1].toLowerCase();
          return itemAgency.includes(agency);
        }
        return false;
      });
      console.log('발신 기관 필터링 후 메시지 수:', filteredItems.length);
    }

    // 실종자 관련 메시지 필터링
    if (filters.missingPersonOnly) {
      filteredItems = filteredItems.filter(item => isMissingPersonMessage(item));
      console.log('실종자 필터링 후 메시지 수:', filteredItems.length);
    }

    // 최신순으로 정렬
    const sortedItems = sortItemsByDate(filteredItems);
    console.log('정렬 후 최종 메시지 수:', sortedItems.length);

    return sortedItems;

  } catch (error) {
    console.error('필터링 처리 중 오류 발생:', error);
    return [];
  }
};

// 실종자 관련 메시지인지 확인하는 함수
const isMissingPersonMessage = (item) => {
  if (!item) return false;

  const message = (item.MSG_CN || item.msg || '').toLowerCase();

  // 발신 기관 추출
  const agencyMatch = message.match(/\[(.*?)\]/);
  const agency = agencyMatch && agencyMatch.length > 1 ? agencyMatch[1].toLowerCase() : '';

  // 경찰청 또는 경찰서 발송 메시지
  const isPoliceAgency = agency.includes('경찰청') || agency.includes('경찰서');

  // 실종자 관련 키워드
  const missingKeywords = ['실종', '미아', '찾습니다', '행방불명', '발견되었습니다', '사람을 찾습니다', '세(남)', '세(여)'];

  // 실종자 키워드 포함 여부
  const containsMissingKeywords = missingKeywords.some(keyword => message.includes(keyword.toLowerCase()));

  // 재난 관련 키워드 (제외할 키워드)
  const disasterKeywords = ['산불', '화재', '지진', '태풍', '홍수', '호우', '코로나', '정전', '사고'];

  // 재난 키워드 포함 여부
  const containsDisasterKeywords = disasterKeywords.some(keyword => message.includes(keyword.toLowerCase()));

  // 실종자 정보 패턴 (나이+성별 패턴)
  const hasAgeGenderPattern = /\d+세.*[남|여]/.test(message) ||
    /\(남,.*세\)/.test(message) ||
    /\(여,.*세\)/.test(message);

  // 실종자 메시지로 판단하는 조건:
  // 1. 경찰청 메시지이면서 실종자 키워드가 있거나
  // 2. 실종자 키워드가 있고 재난 키워드가 없으면서 나이+성별 패턴이 있는 경우
  return (isPoliceAgency && containsMissingKeywords) ||
    (containsMissingKeywords && !containsDisasterKeywords && hasAgeGenderPattern);
};

// 메시지에서 발신 기관 추출하는 함수
const extractAgency = (message) => {
  if (!message) return '';

  const matches = message.match(/\[(.*?)\]/);
  if (matches && matches.length > 1) {
    return matches[1];
  }

  return '';
};

// 날짜 형식 변환 함수
const formatDate = (dateStr) => {
  if (!dateStr) return '';

  // YYYYMMDD 형식 처리
  if (dateStr.length === 8) {
    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
  }

  // 이미 포맷된 날짜는 그대로 반환
  return dateStr;
};

// 🔥 추가: 캐시 갱신 API
const refreshCache = async () => {
  try {
    const response = await fetch(`${API_URL}/api/disaster/refresh-cache`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP 오류: ${response.status}`);
    }

    const result = await response.json();
    console.log('재난문자 캐시 갱신 완료:', result);
    return result;
  } catch (error) {
    console.error('재난문자 캐시 갱신 실패:', error);
    throw error;
  }
};

// 🔥 추가: 캐시 상태 조회 API  
const getCacheStatus = async () => {
  try {
    const response = await fetch(`${API_URL}/api/disaster/cache-status`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP 오류: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('재난문자 캐시 상태 조회 실패:', error);
    return { success: false, error: error.message };
  }
};

// 외부로 내보낼 메서드들
const disasterMessagesApi = {
  fetchDisasterMessages,
  filterDisasterMessages,
  isMissingPersonMessage,
  extractAgency,
  formatDate,
  sortItemsByDate,
  refreshCache,        // 🔥 추가
  getCacheStatus       // 🔥 추가
};

export default disasterMessagesApi;